import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  AdminSummary,
  AiQuotaHistoryResponse,
  AvailableTermin,
  Company,
  CreateInvoiceResponse,
  InvoiceCreatePayload,
  InvoiceListFilter,
  InvoiceListResponse,
  InvoiceRecord,
  InvoiceUpdatePayload,
  MySummary,
  PaymentInfo,
  Product,
  SpUsageByProject,
  SpUsageSummary,
  TermOfPayment,
  TermOfPaymentListResponse,
  TermOfPaymentCreatePayload,
  TermOfPaymentUpdatePayload,
} from './types';

export interface BillingApi {
  // Admin endpoints
  getAdminSummary(): Promise<AdminSummary>;
  getCompanies(): Promise<Company[]>;
  getProducts(): Promise<Product[]>;
  getCompanyAiQuotaHistory(
    companySlug: string,
    limit?: number,
    offset?: number,
  ): Promise<AiQuotaHistoryResponse>;

  // User endpoints
  getMySummary(): Promise<MySummary>;
  getMyAiQuotaHistory(
    limit?: number,
    offset?: number,
  ): Promise<AiQuotaHistoryResponse>;
  getMySpUsage(options?: {
    from?: string;
    to?: string;
    type?: string;
  }): Promise<SpUsageByProject>;

  // SP Usage endpoints
  getSpUsageSummary(): Promise<SpUsageSummary>;
  getCompanySpUsageProjects(
    companySlug: string,
    options?: { from?: string; to?: string; type?: string },
  ): Promise<SpUsageByProject>;

  // Invoice endpoints (legacy)
  getInvoices(options?: {
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    companyId?: string;
    productId?: string;
    status?: string;
    invoiceNumber?: string;
  }): Promise<InvoiceListResponse>;
  createInvoice(data: {
    total_amount: number;
    total_sp: number;
    currency: string;
    draft: boolean;
    company_id: string;
    sp: number;
    emails?: string[];
    id?: number;
  }): Promise<CreateInvoiceResponse>;
  sendInvoice(
    invoiceId: number,
  ): Promise<{ success?: boolean; message?: string }>;
  getInvoicePayment(invoiceId: number): Promise<PaymentInfo | null>;

  // Enhanced Invoice endpoints
  getInvoicesEnhanced(filter?: InvoiceListFilter): Promise<InvoiceListResponse>;
  getInvoiceById(id: number): Promise<InvoiceRecord>;
  createInvoiceEnhanced(
    data: InvoiceCreatePayload,
  ): Promise<CreateInvoiceResponse>;
  updateInvoiceEnhanced(
    data: InvoiceUpdatePayload,
  ): Promise<CreateInvoiceResponse>;
  updateInvoiceStatus(
    id: number,
    status: string,
    options?: {
      referenceNumber?: string;
      paymentMethod?: string;
      paymentNotes?: string;
    },
  ): Promise<{ success: boolean; message: string; data?: InvoiceRecord }>;
  deleteInvoice(id: number): Promise<{ success: boolean; message: string }>;
  getAvailableTermins(topId: number): Promise<AvailableTermin[]>;

  // Term of Payment endpoints
  getTermOfPayments(options?: {
    limit?: number;
    offset?: number;
    companyId?: number;
    status?: string;
    search?: string;
  }): Promise<TermOfPaymentListResponse>;
  getTermOfPaymentById(id: number): Promise<TermOfPayment>;
  createTermOfPayment(
    data: TermOfPaymentCreatePayload,
  ): Promise<{ success: boolean; message: string; data?: TermOfPayment }>;
  updateTermOfPayment(
    data: TermOfPaymentUpdatePayload,
  ): Promise<{ success: boolean; message: string; data?: TermOfPayment }>;
  deleteTermOfPayment(
    id: number,
  ): Promise<{ success: boolean; message: string }>;
}

export const billingApiRef = createApiRef<BillingApi>({
  id: 'plugin.billing.api',
});

export class BillingClient implements BillingApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('billing-portal');
  }

  private async fetch<T>(
    path: string,
    method: 'GET' | 'POST' = 'GET',
    body?: unknown,
  ): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    };
    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, options);
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody?.message ??
          errBody?.error ??
          `Billing API ${path} failed: ${response.status} ${response.statusText}`,
      );
    }
    if (response.status === 204) {
      return undefined as unknown as T;
    }
    return response.json();
  }

  // Admin endpoints
  async getAdminSummary(): Promise<AdminSummary> {
    return this.fetch<AdminSummary>('/summary');
  }

  async getCompanies(): Promise<Company[]> {
    const result = await this.fetch<Company[]>('/proxy/companies.get', 'POST');
    return Array.isArray(result) ? result : [];
  }

  async getProducts(): Promise<Product[]> {
    const result = await this.fetch<Product[]>('/proxy/products.get', 'POST');
    return Array.isArray(result) ? result : [];
  }

  async getCompanyAiQuotaHistory(
    companySlug: string,
    limit = 50,
    offset = 0,
  ): Promise<AiQuotaHistoryResponse> {
    return this.fetch<AiQuotaHistoryResponse>(
      `/companies/${encodeURIComponent(
        companySlug,
      )}/ai-quota-history?limit=${limit}&offset=${offset}`,
    );
  }

  // User endpoints
  async getMySummary(): Promise<MySummary> {
    return this.fetch<MySummary>('/my/summary');
  }

  async getMyAiQuotaHistory(
    limit = 20,
    offset = 0,
  ): Promise<AiQuotaHistoryResponse> {
    return this.fetch<AiQuotaHistoryResponse>(
      `/my/ai-quota-history?limit=${limit}&offset=${offset}`,
    );
  }

  async getMySpUsage(options?: {
    from?: string;
    to?: string;
    type?: string;
  }): Promise<SpUsageByProject> {
    const params = new URLSearchParams();
    if (options?.from) params.append('from', options.from);
    if (options?.to) params.append('to', options.to);
    if (options?.type) params.append('type', options.type);
    const qs = params.toString();
    return this.fetch<SpUsageByProject>(`/my/sp-usage${qs ? `?${qs}` : ''}`);
  }

  // SP Usage endpoints
  async getSpUsageSummary(): Promise<SpUsageSummary> {
    return this.fetch<SpUsageSummary>('/sp-usage/summary');
  }

  async getCompanySpUsageProjects(
    companySlug: string,
    options?: { from?: string; to?: string; type?: string },
  ): Promise<SpUsageByProject> {
    const params = new URLSearchParams();
    if (options?.from) params.append('from', options.from);
    if (options?.to) params.append('to', options.to);
    if (options?.type) params.append('type', options.type);
    const qs = params.toString();
    return this.fetch<SpUsageByProject>(
      `/sp-usage/companies/${encodeURIComponent(companySlug)}/projects${
        qs ? `?${qs}` : ''
      }`,
    );
  }

  // Invoice endpoints
  async getInvoices(options?: {
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    companyId?: string;
    productId?: string;
    status?: string;
    invoiceNumber?: string;
  }): Promise<InvoiceListResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    if (options?.sortBy) params.append('sort_by', options.sortBy);
    if (options?.sortOrder) params.append('sort_order', options.sortOrder);
    if (options?.companyId) params.append('company_id', options.companyId);
    if (options?.productId) params.append('product_id', options.productId);
    if (options?.status) params.append('status', options.status);
    if (options?.invoiceNumber)
      params.append('invoice_number', options.invoiceNumber);
    const qs = params.toString();
    return this.fetch<InvoiceListResponse>(
      `/invoice-records${qs ? `?${qs}` : ''}`,
    );
  }

  async createInvoice(data: {
    total_amount: number;
    total_sp: number;
    currency: string;
    draft: boolean;
    company_id: string;
    sp: number;
    emails?: string[];
    id?: number;
  }): Promise<CreateInvoiceResponse> {
    return this.fetch<CreateInvoiceResponse>('/invoice-records', 'POST', data);
  }

  async sendInvoice(
    invoiceId: number,
  ): Promise<{ success?: boolean; message?: string }> {
    return this.fetch('/invoice-records/send', 'POST', {
      invoice_id: invoiceId,
    });
  }

  async getInvoicePayment(invoiceId: number): Promise<PaymentInfo | null> {
    return this.fetch<PaymentInfo | null>(`/invoice-payments/${invoiceId}`);
  }

  // Term of Payment endpoints
  async getTermOfPayments(options?: {
    limit?: number;
    offset?: number;
    companyId?: number;
    status?: string;
    search?: string;
  }): Promise<TermOfPaymentListResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    if (options?.companyId)
      params.append('company_id', String(options.companyId));
    if (options?.status) params.append('status', options.status);
    if (options?.search) params.append('search', options.search);
    const qs = params.toString();
    return this.fetch<TermOfPaymentListResponse>(
      `/term-of-payments${qs ? `?${qs}` : ''}`,
    );
  }

  async getTermOfPaymentById(id: number): Promise<TermOfPayment> {
    return this.fetch<TermOfPayment>(`/term-of-payments/${id}`);
  }

  async createTermOfPayment(
    data: TermOfPaymentCreatePayload,
  ): Promise<{ success: boolean; message: string; data?: TermOfPayment }> {
    return this.fetch('/term-of-payments', 'POST', data);
  }

  async updateTermOfPayment(
    data: TermOfPaymentUpdatePayload,
  ): Promise<{ success: boolean; message: string; data?: TermOfPayment }> {
    return this.fetch('/term-of-payments', 'POST', { ...data, _method: 'PUT' });
  }

  async deleteTermOfPayment(
    id: number,
  ): Promise<{ success: boolean; message: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/term-of-payments/${id}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody?.message ??
          errBody?.error ??
          `Failed to delete term of payment: ${response.status}`,
      );
    }
    return response.json();
  }

  // Enhanced Invoice endpoints
  async getInvoicesEnhanced(
    filter?: InvoiceListFilter,
  ): Promise<InvoiceListResponse> {
    const params = new URLSearchParams();
    if (filter?.search) params.append('search', filter.search);
    if (filter?.status) params.append('status', filter.status);
    if (filter?.companyId)
      params.append('company_id', String(filter.companyId));
    if (filter?.serviceType) params.append('service_type', filter.serviceType);
    if (filter?.invoiceDateFrom)
      params.append('invoice_date_from', filter.invoiceDateFrom);
    if (filter?.invoiceDateTo)
      params.append('invoice_date_to', filter.invoiceDateTo);
    if (filter?.dueDateFrom) params.append('due_date_from', filter.dueDateFrom);
    if (filter?.dueDateTo) params.append('due_date_to', filter.dueDateTo);
    if (filter?.sortBy) params.append('sort_by', filter.sortBy);
    if (filter?.sortOrder) params.append('sort_order', filter.sortOrder);
    if (filter?.limit) params.append('limit', String(filter.limit));
    if (filter?.offset) params.append('offset', String(filter.offset));
    const qs = params.toString();
    console.log('Fetching invoices with query:', qs);
    return this.fetch<InvoiceListResponse>(
      `/invoice-records/enhanced${qs ? `?${qs}` : ''}`,
    );
  }

  async getInvoiceById(id: number): Promise<InvoiceRecord> {
    const response = await this.fetch<{ data: InvoiceRecord }>(
      `/invoice-records/${id}`,
    );
    return response.data;
  }

  async createInvoiceEnhanced(
    data: InvoiceCreatePayload,
  ): Promise<CreateInvoiceResponse> {
    return this.fetch<CreateInvoiceResponse>(
      '/invoice-records/enhanced',
      'POST',
      data,
    );
  }

  async updateInvoiceEnhanced(
    data: InvoiceUpdatePayload,
  ): Promise<CreateInvoiceResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/invoice-records/${data.id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody?.message ??
          errBody?.error ??
          `Failed to update invoice: ${response.status}`,
      );
    }
    return response.json();
  }

  async updateInvoiceStatus(
    id: number,
    status: string,
    options?: {
      referenceNumber?: string;
      paymentMethod?: string;
      paymentNotes?: string;
    },
  ): Promise<{ success: boolean; message: string; data?: InvoiceRecord }> {
    const baseUrl = await this.getBaseUrl();
    const body: Record<string, unknown> = { status };
    if (options?.referenceNumber) {
      body.reference_number = options.referenceNumber;
    }
    if (options?.paymentMethod) {
      body.payment_method = options.paymentMethod;
    }
    if (options?.paymentNotes) {
      body.payment_notes = options.paymentNotes;
    }
    const response = await this.fetchApi.fetch(
      `${baseUrl}/invoice-records/${id}/status`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody?.message ??
          errBody?.error ??
          `Failed to update invoice status: ${response.status}`,
      );
    }
    return response.json();
  }

  async deleteInvoice(
    id: number,
  ): Promise<{ success: boolean; message: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/invoice-records/${id}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody?.message ??
          errBody?.error ??
          `Failed to delete invoice: ${response.status}`,
      );
    }
    return response.json();
  }

  async getAvailableTermins(topId: number): Promise<AvailableTermin[]> {
    const response = await this.fetch<{ data: AvailableTermin[] }>(
      `/term-of-payments/${topId}/available-termins`,
    );
    return response.data || [];
  }
}
